// Service: Asset Aggregation
import type { UserSession } from '../hooks/useSessionPersistence';

// Define the unified asset structure for the distribution view
export interface UnifiedAsset {
    // Core identification
    id: string;
    sourceType: 'financial' | 'property' | 'personal' | 'pet';
    sourceKey: string;  // e.g., 'cashAccounts', 'properties', 'personalItems'

    // Display info 
    name: string;
    category: string;  // For UI categorization: 'Financial', 'Properties', 'Personal Items', 'Pets'
    displayType: string; // specific type e.g., 'Checking Account', 'House', 'Jewelry'
    description?: string;

    // Value (optional - pets/documents may not have)
    value?: number;

    // Pre-existing beneficiary (if already assigned in source workflow)
    // This is the "trickle down" data
    existingBeneficiaries?: {
        contactId: string;
        name: string;
        percentage?: number; // Usually undefined for source assets, but good to have in structure
    }[];

    // Metadata
    institution?: string;
    location?: string;

    // Legal / Probate Logic
    assignmentType: 'legal_beneficiary' | 'executor_guidance'; // Keeping for internal logic if needed, but recipientLabel is the display
    recipientLabel: 'Beneficiary' | 'Intended Recipient';

    // Unique composite ID to prevent collisions across sources
    uniqueId: string;
}

// Row shapes from session / backups (fields vary by source)
type SessionAssetRow = Record<string, unknown>;

// Helper to safely get data from session or (optionally) localStorage backup
const getFromSessionOrBackup = <T extends SessionAssetRow>(
    sessionData: unknown,
    backupKey: string,
    transformFn: (backupData: unknown) => T[] = (d) => (Array.isArray(d) ? (d as T[]) : []),
    useLocalBackups = true
): T[] => {
    // 1. Try session data first (authoritative)
    if (Array.isArray(sessionData) && sessionData.length > 0) {
        return sessionData as T[];
    }

    // 2. Optionally try localStorage backup (disabled for executor views)
    if (useLocalBackups) {
        try {
            const backup = localStorage.getItem(backupKey);
            if (backup) {
                const parsed = JSON.parse(backup);
                // Some backups are the array itself, some are objects { cashAccounts: [...] }
                // The transformFn handles extracting the array from the parsed object
                const extracted = transformFn(parsed);
                if (Array.isArray(extracted) && extracted.length > 0) {
                    return extracted;
                }
            }
        } catch (e) {
            console.warn(`[AssetAggregation] Backup load failed for ${backupKey}`, e);
        }
    }

    return [];
};

export interface GetAllAssetsOptions {
    /** When false, localStorage backup keys are never consulted (safe for executor routes). */
    useLocalBackups?: boolean;
}

export const assetAggregationService = {
    /**
     * Aggregates all assets from the user session into a single unified list.
     *
     * Pass `{ useLocalBackups: false }` on executor routes to prevent the
     * logged-in executor's browser backup data from leaking into estate views.
     */
    getAllAssets: (session: UserSession, options: GetAllAssetsOptions = {}): UnifiedAsset[] => {
        const { useLocalBackups = true } = options;
        // Allow running even if session.taskProgress is empty
        const progress = session?.taskProgress || {};

        const unifiedAssets: UnifiedAsset[] = [];

        // 1. Process Financial Assets
        // Cash Accounts
        const cashAccounts = getFromSessionOrBackup(
            progress.financial_assets?.answers?.cashAccounts,
            'cash-accounts-backup',
            undefined,
            useLocalBackups
        );
        cashAccounts.forEach((acc) => {
            unifiedAssets.push({
                id: String(acc.id ?? ''),
                uniqueId: `financial_cash_${String(acc.id ?? '')}`,
                sourceType: 'financial',
                sourceKey: 'cashAccounts',
                name: String(acc.name ?? ''),
                category: 'Financial',
                displayType: mapFinancialType(String(acc.type ?? 'cash')),
                description: `Ending in ${String(acc.accountNumber ?? '').slice(-4) || 'xxxx'}`,
                value: typeof acc.balance === 'number' ? acc.balance : Number(acc.balance),
                institution: acc.institution as string | undefined,
                existingBeneficiaries: acc.beneficiaries as UnifiedAsset['existingBeneficiaries'],
                assignmentType: 'legal_beneficiary',
                recipientLabel: 'Beneficiary'
            });
        });

        // Investment Accounts
        const investmentAccounts = getFromSessionOrBackup(
            progress.financial_assets?.answers?.investmentAccounts,
            'investment-accounts-backup',
            undefined,
            useLocalBackups
        );
        investmentAccounts.forEach((acc) => {
            unifiedAssets.push({
                id: String(acc.id ?? ''),
                uniqueId: `financial_invest_${String(acc.id ?? '')}`,
                sourceType: 'financial',
                sourceKey: 'investmentAccounts',
                name: String(acc.name ?? ''),
                category: 'Financial',
                displayType: mapFinancialType(String(acc.type ?? 'investment')),
                description: `Ending in ${String(acc.accountNumber ?? '').slice(-4) || 'xxxx'}`,
                value: typeof acc.balance === 'number' ? acc.balance : Number(acc.balance),
                institution: acc.institution as string | undefined,
                existingBeneficiaries: [], 
                assignmentType: 'executor_guidance',
                recipientLabel: 'Intended Recipient'
            });
        });

        // Retirement Accounts
        const retirementAccounts = getFromSessionOrBackup(
            progress.financial_assets?.answers?.retirementAccounts,
            'retirement-accounts-backup',
            undefined,
            useLocalBackups
        );
        retirementAccounts.forEach((acc) => {
            unifiedAssets.push({
                id: String(acc.id ?? ''),
                uniqueId: `financial_retire_${String(acc.id ?? '')}`,
                sourceType: 'financial',
                sourceKey: 'retirementAccounts',
                name: String(acc.name ?? ''),
                category: 'Financial',
                displayType: mapFinancialType(String(acc.type ?? 'retirement')),
                description: `Ending in ${String(acc.accountNumber ?? '').slice(-4) || 'xxxx'}`,
                value: typeof acc.balance === 'number' ? acc.balance : Number(acc.balance),
                institution: acc.institution as string | undefined,
                existingBeneficiaries: acc.beneficiaries as UnifiedAsset['existingBeneficiaries'],
                assignmentType: 'legal_beneficiary',
                recipientLabel: 'Beneficiary'
            });
        });

        // Other Financial Assets
        const otherFinancial = getFromSessionOrBackup(
            progress.financial_assets?.answers?.otherFinancialAssets,
            'other-financial-assets-backup',
            undefined,
            useLocalBackups
        );
        otherFinancial.forEach((acc) => {
            unifiedAssets.push({
                id: String(acc.id ?? ''),
                uniqueId: `financial_other_${String(acc.id ?? '')}`,
                sourceType: 'financial',
                sourceKey: 'otherFinancialAssets',
                name: String(acc.name ?? ''),
                category: 'Financial',
                displayType: mapFinancialType(String(acc.type ?? 'other')),
                description: acc.policyNumber ? `Policy: ${String(acc.policyNumber)}` : undefined,
                value: typeof acc.value === 'number' ? acc.value : Number(acc.value),
                institution: acc.institution as string | undefined,
                existingBeneficiaries: acc.beneficiaries as UnifiedAsset['existingBeneficiaries'],
                assignmentType: 'legal_beneficiary',
                recipientLabel: 'Beneficiary'
            });
        });

        // Income Sources
        const incomeSources = getFromSessionOrBackup(
            progress.financial_assets?.answers?.incomeSources,
            'income-sources-backup',
            undefined,
            useLocalBackups
        );
        incomeSources.forEach((source) => {
            unifiedAssets.push({
                id: String(source.id ?? ''),
                uniqueId: `financial_income_${String(source.id ?? '')}`,
                sourceType: 'financial',
                sourceKey: 'incomeSources',
                name: String(source.sourceName ?? ''),
                category: 'Financial',
                displayType: mapIncomeSourceType(String(source.type ?? 'other')),
                description: String(source.payerInstitution ?? ''),
                value: typeof source.approximateAmount === 'number'
                    ? source.approximateAmount
                    : undefined,
                institution: source.payerInstitution as string | undefined,
                existingBeneficiaries: [],
                assignmentType: 'executor_guidance',
                recipientLabel: 'Intended Recipient',
            });
        });

        // 2. Process Properties (No beneficiaries -> Executor Guidance)
        const properties = getFromSessionOrBackup(
            progress.properties?.answers?.properties,
            'properties-backup',
            undefined,
            useLocalBackups
        );
        properties.forEach((prop) => {
            const ptype = prop.type;
            const typeStr = typeof ptype === 'string' && ptype ? (ptype.charAt(0).toUpperCase() + ptype.slice(1)) : 'Property';
            unifiedAssets.push({
                id: String(prop.id ?? ''),
                uniqueId: `property_${String(prop.id ?? '')}`,
                sourceType: 'property',
                sourceKey: 'properties',
                name: String(prop.displayName ?? prop.address ?? ''),
                category: 'Real Estate',
                displayType: typeStr,
                description: prop.address as string | undefined,
                value: typeof prop.currentValue === 'number' ? prop.currentValue : Number(prop.currentValue),
                existingBeneficiaries: [],
                assignmentType: 'executor_guidance',
                recipientLabel: 'Intended Recipient'
            });
        });

        // 3. Process Personal Items
        const personalItems = getFromSessionOrBackup(
            progress.personal?.answers?.personalItems,
            'personal-items-backup',
            (d) => Array.isArray(d) ? d as SessionAssetRow[] : ((d as Record<string, unknown>).items as SessionAssetRow[] || []),
            useLocalBackups
        );
        personalItems.forEach((item) => {
            unifiedAssets.push({
                id: String(item.id ?? ''),
                uniqueId: `personal_${String(item.id ?? '')}`,
                sourceType: 'personal',
                sourceKey: 'personalItems',
                name: String(item.name ?? ''),
                category: 'Personal Items',
                displayType: item.category ? mapPersonalCategory(String(item.category)) : 'Item',
                description: item.description as string | undefined,
                value: typeof item.estimatedValue === 'number' ? item.estimatedValue : Number(item.estimatedValue),
                location: item.location as string | undefined,
                existingBeneficiaries: [],
                assignmentType: 'executor_guidance',
                recipientLabel: 'Intended Recipient'
            });
        });

        // 4. Process Pets
        // Session path: session.taskProgress.pets?.answers?.petsAndDependants?.pets
        // Backup key: 'pets-dependants-backup' -> object { pets: [], dependants: [] }
        const petsTask = progress.pets as
            | { answers?: { petsAndDependants?: { pets?: SessionAssetRow[] } } }
            | undefined;

        const petsData = getFromSessionOrBackup(
            petsTask?.answers?.petsAndDependants?.pets,
            'pets-dependants-backup',
            (d) => (d as Record<string, unknown>).pets as SessionAssetRow[] || [],
            useLocalBackups
        );

        petsData.forEach((pet) => {
            const ptype = pet.type;
            const display = typeof ptype === 'string' && ptype ? (ptype.charAt(0).toUpperCase() + ptype.slice(1)) : 'Pet';
            unifiedAssets.push({
                id: String(pet.id ?? ''),
                uniqueId: `pet_${String(pet.id ?? '')}`,
                sourceType: 'pet',
                sourceKey: 'pets',
                name: String(pet.name ?? ''),
                category: 'Pets',
                displayType: display,
                description: pet.preferredCaregiver ? `Preferred Caregiver: ${String(pet.preferredCaregiver)}` : undefined,
                value: 0,
                existingBeneficiaries: [],
                assignmentType: 'executor_guidance',
                recipientLabel: 'Intended Recipient'
            });
        });

        return unifiedAssets;
    },

    /**
     * Helper to get total value of all assets (where value is numeric)
     */
    getTotalValue: (assets: UnifiedAsset[]): number => {
        return assets.reduce((sum, asset) => sum + (asset.value || 0), 0);
    },

    /**
     * Helper to group assets by category for the sidebar navigation
     */
    groupAssetsByCategory: (assets: UnifiedAsset[]): Record<string, UnifiedAsset[]> => {
        return assets.reduce((acc, asset) => {
            if (!acc[asset.category]) {
                acc[asset.category] = [];
            }
            acc[asset.category].push(asset);
            return acc;
        }, {} as Record<string, UnifiedAsset[]>);
    }
};

// --- Helpers ---

function mapFinancialType(type: string): string {
    const map: Record<string, string> = {
        'checking': 'Checking Account',
        'savings': 'Savings Account',
        'money_market': 'Money Market',
        'cd': 'Certificate of Deposit',
        'stocks': 'Stocks',
        'bonds': 'Bonds',
        'mutual_funds': 'Mutual Funds',
        'etfs': 'ETFs',
        'traditional_ira': 'Traditional IRA',
        '401k': '401(k)',
        'roth_ira': 'Roth IRA',
        'pension': 'Pension',
        'life_insurance': 'Life Insurance',
        'annuity': 'Annuity',
        'trust': 'Trust Fund',
        'business': 'Business Interest'
    };
    return map[type] || type.charAt(0).toUpperCase() + type.slice(1);
}

function mapIncomeSourceType(type: string): string {
    const map: Record<string, string> = {
        'social_security':   'Social Security',
        'pension':           'Pension',
        'rental_income':     'Rental Income',
        'investment_income': 'Investment Income',
        'business_income':   'Business Income',
        'annuity':           'Annuity',
        'other':             'Other Income',
    };
    return map[type] || type.replace(/_/g, ' ');
}

function mapPersonalCategory(cat: string): string {
    const map: Record<string, string> = {
        'documents': 'Document',
        'physical_assets': 'Physical Asset',
        'collectibles': 'Collectible',
        'electronics': 'Electronics',
        'digital_assets': 'Digital Asset',
        'custom': 'Custom Item'
    };
    return map[cat] || 'Item';
}
